import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Error } from '~/Components';
import { ThirdPartyTable } from './ThirdPartyTable';
import { PrivateLayout } from '~/Layout/PrivateLayout';
import { Trans, useTranslation } from 'react-i18next';
import { useRequestChannels } from '~/requests/adapterSvc/useRequestChannels';
import { ToastVariant, useProvider, useToastMessage, useNavigation } from '~/Hooks';
import { useLocationListWithStatus } from '~/Hooks/useLocationsList';
import { JumpingLoader } from '~/UIKit';
import { IIntegrationChannel, IntegrationTypes } from '~/Models/DTO/IntegrationChannels.model';
const ThirdPartyIntegrations = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentProvider } = useProvider();
  const providerUUID = currentProvider?.uuid as string;
  const providerDomain = currentProvider?.domain as string;

  const { integration: integrationType } = useParams<{ integration: string }>();

  const [magiclineItegrations, setMagiclineIntegrations] = useState<IIntegrationChannel[]>([]);
  const [eversportsItegrations, setEversportsIntegrations] = useState<IIntegrationChannel[]>([]);
  const [virtuagymIntegration, setVirtuagymIntegration] = useState<IIntegrationChannel[]>([]);
  const [zingfitIntegration, setZingfitIntegration] = useState<IIntegrationChannel[]>([]);
  const [mindbodyIntegration, setMindbodyIntegration] = useState<IIntegrationChannel[]>([]);
  const [virtuagymV1Integration, setVirtuagymV1Integration] = useState<IIntegrationChannel[]>([]);
  const [resamaniaIntegration, setResamaniaIntegration] = useState<IIntegrationChannel[]>([]);

  const { showToast } = useToastMessage();

  const defaultQuery = { providerId: providerUUID || '' };

  const { data: integrations, error: integrationsError, isLoading } = useRequestChannels({
    providerId: providerUUID,
  });

  const {
    data: locations,
    error: locationsError,
    isLoading: locationsLoading,
  } = useLocationListWithStatus(defaultQuery);

  const separateIntegrations = () => {
    const magicline = integrations?.data?.filter(
      (obj: IIntegrationChannel) => obj.integration === 'magicline',
    );
    if (magicline) {
      setMagiclineIntegrations(magicline);
    }

    const eversports = integrations?.data?.filter(
      (obj: IIntegrationChannel) => obj.integration === 'eversports',
    );
    if (eversports) {
      setEversportsIntegrations(eversports);
    }
    const virtuagym = integrations?.data?.filter(
      (obj: IIntegrationChannel) => obj.integration === 'virtuagym',
    );
    if (virtuagym) {
      setVirtuagymIntegration(virtuagym);
    }
    const zingfit = integrations?.data?.filter(
      (obj: IIntegrationChannel) => obj.integration === 'zingfit',
    );
    if (zingfit) {
      setZingfitIntegration(zingfit);
    }
    const mindbody = integrations?.data?.filter(
      (obj: IIntegrationChannel) => obj.integration === 'mindbody',
    );
    if (mindbody) {
      setMindbodyIntegration(mindbody);
    }

    const virtuagymV1 = integrations?.data?.filter(
      (obj: IIntegrationChannel) => obj.integration === 'virtuagymv1',
    );
    if (virtuagymV1) {
      setVirtuagymV1Integration(virtuagymV1);
    }

    const resamania = integrations?.data?.filter(
      (obj: IIntegrationChannel) => obj.integration === 'resamania',
    );
    if (resamania) {
      setResamaniaIntegration(resamania);
    }
  };

  const { navigate } = useNavigation();

  const goToIntegrationCreation = () => {
    navigate(`/provider/${providerDomain}/integrations/${integrationType}/new`);
  };

  const determineTitle = () => {
    switch (integrationType) {
      case IntegrationTypes.Magicline:
        return `${t('magicline.title')}: ${magiclineItegrations.length}`;
      case IntegrationTypes.Eversports:
        return `${t('eversports.title')}: ${eversportsItegrations.length}`;
      case IntegrationTypes.Virtuagym:
        return `${t('virtuagym.title')}: ${virtuagymIntegration.length}`;
      case IntegrationTypes.Zingfit:
        return `${t('zingfit.title')}: ${zingfitIntegration.length}`;
      case IntegrationTypes.VirtuagymV1:
        return `${t('virtuagymV1.title')}: ${virtuagymV1Integration.length}`;
      case IntegrationTypes.Mindbody:
        return `${t('mindbody.title')}: ${mindbodyIntegration.length}`;
      case IntegrationTypes.Resamania:
        return `${t('resamania.title')}: ${resamaniaIntegration.length}`;
      default:
        return '';
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const successParam = urlParams.get('success');
    const locationParam = urlParams.get('location');

    if (successParam === 'true' && locations) {
      const matchingLocation = locations.find((location) => location.uuid === locationParam);

      if (matchingLocation) {
        showToast(
          'Magicline',
          <Trans
            i18nKey="magicline.integration.successfully.saved"
            values={{ location: matchingLocation.name }}
          />,
          ToastVariant.SUCCESS,
          true,
        );
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const filterIntegrations = [
      'magicline',
      'eversports',
      'virtuagym',
      'zingfit',
      'virtuagymv1',
      'mindbody',
      'resamania',
    ];
    if (filterIntegrations.indexOf(integrationType) === -1) {
      history.push('/');
    }
    separateIntegrations();
  }, [history, integrationType, integrations?.data]);

  return (
    <PrivateLayout
      title={determineTitle()}
      actionName={t('app.add.form')}
      onActionTaken={goToIntegrationCreation}
    >
      <Error error={integrationsError} />
      <Error error={locationsError} />
      {isLoading || locationsLoading ? (
        <JumpingLoader />
      ) : (
        <ThirdPartyTable
          integrations={integrations?.data}
          locations={locations}
          integrationType={integrationType}
        />
      )}
    </PrivateLayout>
  );
};

export default ThirdPartyIntegrations;
